import { useCallback, useState } from "react";

export interface UseMutationResult<TIn, TOut> {
  mutate: (input: TIn) => Promise<TOut>;
  loading: boolean;
  error: Error | undefined;
  reset: () => void;
}

/**
 * Lehký mutation hook bez cache. Volá fn, drží loading/error state.
 * Žádná invalidate cache - po úspěšné mutaci caller volá `refetch()` z useAsync.
 */
export const useMutation = <TIn, TOut>(fn: (input: TIn) => Promise<TOut>): UseMutationResult<TIn, TOut> => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  const mutate = useCallback(
    async (input: TIn): Promise<TOut> => {
      setLoading(true);
      setError(undefined);
      try {
        const result = await fn(input);
        return result;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fn]
  );

  const reset = useCallback(() => {
    setLoading(false);
    setError(undefined);
  }, []);

  return { mutate, loading, error, reset };
};
